/**
 * Hand-tuned blueprints the Sidequest Author editor (Variant B) offers as
 * multi-select chips. When the player enters an authored sub-level,
 * RunnerScene walks each selected set piece's entity list and spawns each
 * one at `(subLevelEntryWorldX + dx, groundTopY + dy)`.
 *
 * Solvability is hand-validated, not enforced — the future Tower Editor
 * follow-up adds A*. All 8 here are completable when combined with any
 * PuzzleKind.
 */

import { PALETTE } from "../config";
import type { SetPiece, SetPieceEntity } from "../types";

/**
 * Per-blueprint entity spawner. The catalog walker calls this once per
 * `SetPieceEntity`, leaving RunnerScene to translate `(kind, worldX, dy)` into
 * the right existing spawn method (`spawnBird` / `spawnGegner` / etc.).
 */
export type SetPieceEntitySpawner = (kind: SetPieceEntity["kind"], worldX: number, dy: number) => void;

export const SET_PIECE_CATALOG: readonly SetPiece[] = [
  {
    id: "bird_trio",
    category: "enemies",
    labelKey: "author.setpiece.bird_trio",
    iconColor: PALETTE.bird,
    entities: [
      { kind: "bird", dx: 80, dy: -10 },
      { kind: "bird", dx: 160, dy: 0 },
      { kind: "bird", dx: 240, dy: 10 },
    ],
  },
  {
    id: "gegner_cluster",
    category: "enemies",
    labelKey: "author.setpiece.gegner_cluster",
    iconColor: PALETTE.gegner,
    entities: [
      { kind: "gegner", dx: 60, dy: 0 },
      { kind: "gegner", dx: 110, dy: 0 },
      { kind: "gegner", dx: 160, dy: 0 },
      { kind: "gegner", dx: 210, dy: 0 },
    ],
  },
  {
    id: "lever_maze",
    category: "enemies",
    labelKey: "author.setpiece.lever_maze",
    iconColor: PALETTE.leverHandle,
    entities: [
      { kind: "gegner", dx: 140, dy: 0 },
      { kind: "lever", dx: 200, dy: 0 },
      { kind: "gegner", dx: 240, dy: 0 },
    ],
  },
  {
    id: "crystal_arc",
    category: "items",
    labelKey: "author.setpiece.crystal_arc",
    iconColor: PALETTE.crystal,
    entities: [
      { kind: "crystal", dx: 80, dy: -20 },
      { kind: "crystal", dx: 160, dy: -30 },
      { kind: "crystal", dx: 240, dy: -20 },
    ],
  },
  {
    id: "heart_sanctuary",
    category: "items",
    labelKey: "author.setpiece.heart_sanctuary",
    iconColor: PALETTE.heart,
    entities: [{ kind: "heart", dx: 120, dy: -40 }],
  },
  {
    id: "boot_dash",
    category: "items",
    labelKey: "author.setpiece.boot_dash",
    iconColor: PALETTE.boot,
    entities: [{ kind: "boot", dx: 60, dy: 0 }],
  },
  {
    id: "feather_cache",
    category: "items",
    labelKey: "author.setpiece.feather_cache",
    iconColor: PALETTE.feather,
    entities: [{ kind: "feather", dx: 180, dy: -30 }],
  },
  {
    id: "twin_portals",
    category: "portals",
    labelKey: "author.setpiece.twin_portals",
    iconColor: PALETTE.tower,
    entities: [
      { kind: "portal", dx: 200, dy: 0 },
      { kind: "portal", dx: 320, dy: 0 },
    ],
  },
] as const;

/** Lookup by id. Returns undefined if the id was dropped from the catalog. */
export function setPieceById(id: string): SetPiece | undefined {
  return SET_PIECE_CATALOG.find((sp) => sp.id === id);
}

/**
 * Walk one set piece's entity list, calling `spawn` for each. Pure — no
 * dependency on Phaser or RunnerScene. The caller is responsible for
 * resolving `(kind, worldX, dy)` into actual game objects.
 */
export function spawnSetPiece(setPiece: SetPiece, baseWorldX: number, spawn: SetPieceEntitySpawner): void {
  for (const entity of setPiece.entities) {
    spawn(entity.kind, baseWorldX + entity.dx, entity.dy);
  }
}
